/**
 * Wishlist Store - AJAX Wrapper for server Wishlist System
 *
 * IMPORTANT: This store ONLY handles UI and AJAX calls.
 * All business logic is handled by PHP (lib_wishlist.inc.php)
 *
 * Responsibilities:
 * - Badge count (initialized from DOM, updated via AJAX)
 * - Loading states
 * - AJAX toggle calls to PHP endpoint
 * - Item list (read from server response)
 */
export default {
  // Runtime state (initialized from window object set by PHP)
  count: 0,
  items: [],

  // UI state only
  loading: false,
  processingItems: new Set(),

  /** Base path for AJAX URLs — uses server's _env or falls back to '/' */
  get basePath() {
    return window._env?.platform?.path || '/';
  },

  init() {
    // Initialize empty, then fetch from server via AJAX
    this.count = 0;
    this.items = [];

    // Fetch initial data from server
    this.updateCount();
  },

  /**
   * Update wishlist count and items from server via AJAX
   */
  async updateCount() {
    try {
      const response = await fetch(this.basePath + 'ajax/wishlist.json', {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (response.ok) {
        const data = await response.json();
        this.count = data.count || 0;
        this.items = data.items || [];
      }
    } catch (error) {
      // Silently fail - count will update on next interaction
    }
  },

  /**
   * Toggle wishlist item via AJAX
   * @param {number} productId
   * @returns {Promise}
   */
  async toggle(productId) {
    if (this.processingItems.has(productId)) {
      return Promise.resolve();
    }

    this.processingItems.add(productId);
    this.loading = true;
    if (window.Loader) {
      window.Loader.show('wishlist-toggle-' + productId, window.tx('loadingUpdating', 'Updating...'));
    }

    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('toggle_wishlist', 'true');

      const response = await fetch(this.basePath + 'ajax/wishlist.json', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.success) {
        // Update from server response
        this.count = data.count;
        this.items = data.items;
        if (window.Notification) {
          window.Notification.success(window.tx('wishlistUpdated', 'Wishlist updated'), 2000);
        }
      } else {
        throw new Error(data.message || 'Failed to toggle wishlist');
      }

      return data;
    } catch (error) {
      if (window.Notification) {
        window.Notification.error(window.tx('wishlistError', 'Wishlist error'), 3000);
      }
      throw error;
    } finally {
      this.processingItems.delete(productId);
      this.loading = false;
      if (window.Loader) {
        window.Loader.hide('wishlist-toggle-' + productId);
      }
    }
  },

  /**
   * Check if product is in wishlist
   * @param {number} productId
   * @returns {boolean}
   */
  isInWishlist(productId) {
    const id = parseInt(productId);
    // Items are now full product objects with id property
    return this.items.some(item => item.id === id);
  },

  /**
   * Remove item from wishlist via AJAX
   * @param {number} productId
   * @returns {Promise}
   */
  async remove(productId) {
    if (this.processingItems.has(productId)) {
      return Promise.resolve();
    }

    this.processingItems.add(productId);
    this.loading = true;
    if (window.Loader) {
      window.Loader.show('wishlist-remove-' + productId, window.tx('loadingRemoving', 'Removing...'));
    }

    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('remove_wishlist', 'true');

      const response = await fetch(this.basePath + 'ajax/wishlist.json', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.success) {
        // Update from server response
        this.count = data.count;
        this.items = data.items;
        if (window.Notification) {
          window.Notification.success(window.tx('wishlistRemoved', 'Item removed from wishlist'), 2000);
        }
      } else {
        throw new Error(data.message || 'Failed to remove from wishlist');
      }

      return data;
    } catch (error) {
      if (window.Notification) {
        window.Notification.error(window.tx('wishlistError', 'Wishlist error'), 3000);
      }
      throw error;
    } finally {
      this.processingItems.delete(productId);
      this.loading = false;
      if (window.Loader) {
        window.Loader.hide('wishlist-remove-' + productId);
      }
    }
  },

  /**
   * Check if product is being processed
   * @param {number} productId
   * @returns {boolean}
   */
  isProcessing(productId) {
    return this.processingItems.has(productId);
  },

  /**
   * Clear all items from wishlist via AJAX
   * @returns {Promise}
   */
  async clear() {
    if (this.loading || this.items.length === 0) {
      return Promise.resolve();
    }

    this.loading = true;
    if (window.Loader) {
      window.Loader.show('wishlist-clear', window.tx('loadingClearing', 'Clearing...'));
    }

    try {
      const formData = new FormData();
      formData.append('clear_wishlist', 'true');

      const response = await fetch(this.basePath + 'ajax/wishlist.json', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.success) {
        // Update from server response
        this.count = 0;
        this.items = [];
        if (window.Notification) {
          window.Notification.success(window.tx('wishlistCleared', 'Wishlist cleared'), 2000);
        }
      } else {
        throw new Error(data.message || 'Failed to clear wishlist');
      }

      return data;
    } catch (error) {
      if (window.Notification) {
        window.Notification.error(window.tx('wishlistError', 'Wishlist error'), 3000);
      }
      throw error;
    } finally {
      this.loading = false;
      if (window.Loader) {
        window.Loader.hide('wishlist-clear');
      }
    }
  },
};
